import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSource, useSources } from "../../../sources";
import { ServerSource } from "../../../sources/serverSource";
import { getTasks, runTask, stopTask, type TaskEntry } from "../../../lib/tasksAdmin";
import {
  describeRun,
  downloadDiagnostic,
  isUnsupported,
  type DiagnosticKind,
} from "../../../lib/troubleshootingAdmin";
import { useServerEvents } from "../../../lib/useServerEvents";
import { useServers } from "../../../lib/servers";
import { Chip, ErrorNote, Spinner } from "../../../components/ui";
import {
  SheetHeader,
  SheetInset,
  SheetLoading,
  SheetNotice,
  SheetRow,
  SheetRows,
  SheetSection,
} from "../../../components/settings-sheet";
import { ServerSettingsGuard } from "../../../components/server-settings";

export const Route = createFileRoute("/_authed/settings/server/$serverId/troubleshooting")({
  component: ServerTroubleshooting,
});

/**
 * Plex's Manage › Troubleshooting: the on-demand maintenance actions plus
 * the diagnostics downloads. The cadence chores (cache cleanup, metadata
 * refresh) stay on the Scheduled Tasks page — this page is for "something
 * is wrong / I just changed a lot" moments.
 */
const ACTION_TASKS = ["optimize-database", "clean-bundles", "backup-database"];

function ServerTroubleshooting() {
  const { serverId } = Route.useParams();
  // Registry subscription: getSource() is a sync snapshot and the queries
  // below would stay disabled forever without the hydration rerender.
  useSources();
  const source = getSource(serverId);
  const isServer = source instanceof ServerSource;
  // Broker-offline: the guard shows the unavailable screen — stop the 15 s
  // fallback poll from hammering a server that cannot answer.
  const { servers } = useServers();
  const online = servers.find((s) => s.id === serverId)?.online ?? false;
  const qc = useQueryClient();

  const tasks = useQuery({
    queryKey: ["settings", "tasks", serverId],
    enabled: isServer && online,
    retry: false,
    // The socket invalidates on task lifecycle; the interval is the
    // fallback for when the WS cannot open (PNA on the direct path).
    refetchInterval: 15_000,
    queryFn: () => getTasks((source as ServerSource).conn),
  });

  useServerEvents(serverId, (ev) => {
    const kind = String((ev.data as { kind?: unknown } | undefined)?.kind ?? "");
    if (ev.type === "task" || (ev.type === "activity" && kind.startsWith("task."))) {
      void qc.invalidateQueries({ queryKey: ["settings", "tasks", serverId] });
    }
  });

  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  async function act(name: string, fn: () => Promise<unknown>) {
    if (!isServer) return;
    setActionBusy(name);
    setError(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ["settings", "tasks", serverId] });
    } catch (e) {
      setError(e);
    } finally {
      setActionBusy(null);
    }
  }

  const items = (tasks.data?.items ?? []).filter((t) => ACTION_TASKS.includes(t.name));
  // One serial worker server-side: while anything runs, every Run waits.
  const anyActive = (tasks.data?.items ?? []).some((t) => t.running || t.queued);
  const unsupported = isUnsupported(tasks.error);

  return (
    <ServerSettingsGuard serverId={serverId} heading="Troubleshooting">
      {(server) => (
        <div>
          <SheetHeader title="Troubleshooting" />

          {unsupported ? (
            <SheetNotice title="Maintenance">
              This server doesn't have maintenance tasks yet — update rago-server.
            </SheetNotice>
          ) : tasks.isLoading ? (
            <SheetLoading />
          ) : (
            <>
              {error || tasks.error ? (
                <SheetInset>
                  <ErrorNote error={error ?? tasks.error} />
                </SheetInset>
              ) : null}

              <SheetSection title="Maintenance" flush>
                <SheetRows>
                  {items.map((t) => (
                    <TaskRow
                      key={t.name}
                      task={t}
                      busy={actionBusy === t.name}
                      blocked={anyActive}
                      onRun={() =>
                        act(t.name, () => runTask((source as ServerSource).conn, t.name))
                      }
                      onStop={() =>
                        act(t.name, () => stopTask((source as ServerSource).conn, t.name))
                      }
                    />
                  ))}
                </SheetRows>
              </SheetSection>

              <SheetSection title="Diagnostics" flush>
                <SheetRows>
                  <DownloadRow
                    title="Download logs"
                    description="Every rago-*.log file (server, scanner, matcher, analysis, transcoder), recent goroutine dumps and a server-info.txt summary as a zip — attach these when reporting a problem."
                    busy={actionBusy === "dl-logs"}
                    onDownload={() =>
                      act("dl-logs", () =>
                        downloadDiagnostic((source as ServerSource).conn, "logs" as DiagnosticKind),
                      )
                    }
                  />
                  <DownloadRow
                    title="Download database"
                    description="Consistent snapshots of the server databases as a zip. A convenience copy, not a backup strategy."
                    busy={actionBusy === "dl-database"}
                    onDownload={() =>
                      act("dl-database", () =>
                        downloadDiagnostic((source as ServerSource).conn, "database"),
                      )
                    }
                  />
                </SheetRows>
              </SheetSection>
            </>
          )}
        </div>
      )}
    </ServerSettingsGuard>
  );
}

function TaskRow(props: {
  task: TaskEntry;
  busy: boolean;
  blocked: boolean;
  onRun: () => void;
  onStop: () => void;
}) {
  const { task } = props;
  const last = describeRun(task);
  return (
    <SheetRow>
      <div className="min-w-0">
        <p className="flex items-center gap-2 text-sm text-[var(--rago-text-1)]">
          {task.title}
          {task.running ? <Chip tone="accent">Running</Chip> : null}
          {task.queued ? <Chip tone="muted">Queued</Chip> : null}
        </p>
        <p className="mt-0.5 text-xs text-[var(--rago-text-3)]">{task.description}</p>
        {!task.running && last ? (
          <p
            className={`mt-1 text-xs ${
              last.tone === "warn" ? "text-[var(--rago-error)]" : "text-[var(--rago-text-3)]"
            }`}
          >
            {last.text}
          </p>
        ) : null}
      </div>
      {task.running || task.queued ? (
        <button
          type="button"
          className="rago-btn rago-btn-quiet rago-btn-sm shrink-0"
          disabled={props.busy}
          onClick={props.onStop}
        >
          {props.busy ? <Spinner size={14} /> : null}
          Stop
        </button>
      ) : (
        <button
          type="button"
          className="rago-btn rago-btn-quiet rago-btn-sm shrink-0"
          disabled={props.busy || props.blocked}
          onClick={props.onRun}
        >
          {props.busy ? <Spinner size={14} /> : null}
          Run
        </button>
      )}
    </SheetRow>
  );
}

function DownloadRow(props: {
  title: string;
  description: string;
  busy: boolean;
  onDownload: () => void;
}) {
  return (
    <SheetRow>
      <div className="min-w-0">
        <p className="text-sm text-[var(--rago-text-1)]">{props.title}</p>
        <p className="mt-0.5 text-xs text-[var(--rago-text-3)]">{props.description}</p>
      </div>
      <button
        type="button"
        className="rago-btn rago-btn-quiet rago-btn-sm shrink-0"
        disabled={props.busy}
        onClick={props.onDownload}
      >
        {props.busy ? <Spinner size={14} /> : null}
        Download
      </button>
    </SheetRow>
  );
}
